/**
 * Tenká fetch vrstva nad katalogovým API (WP2), base `/api/v1` (proxy ve Vite).
 * Typy jsou zrcadlem Pydantic schémat v `apps/api/src/kabinet/api/v1/schemas.py`.
 */

export const API_BASE = '/api/v1';

/* Enumy (zrcadlo kabinet.domain.enums) */

export type ProductStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
export type ContentType = 'PDF' | 'VIDEO' | 'APP';
export type FulfillmentType = 'EMAIL' | 'DOWNLOAD' | 'STREAM' | 'LICENSE_KEY';
export type ProductSort = 'newest' | 'price_asc' | 'price_desc' | 'title';
export type PaymentMethod = 'QR' | 'CARD';
export type OrderStatus =
  | 'CREATED'
  | 'AWAITING_QR'
  | 'AWAITING_CARD'
  | 'PAID'
  | 'DELIVERED'
  | 'EXPIRED'
  | 'CANCELLED'
  | 'REFUNDED';

/* Předmět (číselník; WP11) */

/** Barva křídy předmětu, pevná paleta (proposal 002 §5.2). */
export type ChalkColor = 'white' | 'yellow' | 'blue' | 'pink' | 'green';

/** Vnořený předmět v kartě/detailu produktu (`subject` je objekt, ne enum). */
export type SubjectRef = {
  slug: string;
  name: string;
  icon: string;
  color: ChalkColor;
};

/** Položka veřejného číselníku `GET /subjects`. */
export type Subject = SubjectRef & {
  sort_order: number;
};

/* Schémata */

export type Category = {
  id: number;
  slug: string;
  name: string;
  icon: string;
  sort_order: number;
  description: string | null;
  parent_id: number | null;
  product_count: number;
  children: Category[];
};

export type CategoryRef = {
  id: number;
  slug: string;
  name: string;
  icon: string;
};

export type ProductCard = {
  id: number;
  slug: string;
  title: string;
  subtitle: string | null;
  price_czk: number;
  subject: SubjectRef;
  content_type: ContentType;
  fulfillment_type: FulfillmentType;
  grade: string | null;
  page_count: number | null;
  duration_s: number | null;
  has_sample: boolean;
  status: ProductStatus;
  /* Kontrakt náhledů karty (WP6). Backend je doplňuje; frontend funguje i bez nich. */
  /** Počet veřejných GALLERY náhledů (pro mobilní štítek). */
  gallery_count?: number;
  /** Až 4 URL malých náhledů pro hover slideshow, seřazené dle sort_order. */
  thumbs?: string[];
  /** VIDEO: krátká němá smyčka (mp4/webm). Zatím zpravidla null (kreslený poster). */
  teaser_url?: string | null;
};

export type ProductList = {
  items: ProductCard[];
  total: number;
  limit: number;
  offset: number;
};

export type GalleryAsset = {
  id: number;
  filename: string;
  mime: string;
  sort_order: number;
};

export type Lesson = {
  id: number;
  title: string;
  order_index: number;
  duration_s: number;
  is_sample: boolean;
};

export type ProductDetail = {
  id: number;
  slug: string;
  title: string;
  subtitle: string | null;
  description_md: string;
  price_czk: number;
  subject: SubjectRef;
  content_type: ContentType;
  fulfillment_type: FulfillmentType;
  grade: string | null;
  page_count: number | null;
  duration_s: number | null;
  has_sample: boolean;
  sample_note: string | null;
  status: ProductStatus;
  created_at: string;
  updated_at: string;
  categories: CategoryRef[];
  gallery: GalleryAsset[];
  lessons: Lesson[];
};

export type SampleAsset = {
  id: number;
  filename: string;
  mime: string;
  size_bytes: number | null;
};

export type Sample = {
  product_slug: string;
  content_type: ContentType;
  has_sample: boolean;
  sample_note: string | null;
  sample_asset: SampleAsset | null;
  /** Veřejná URL ukázkového souboru (bez platby/podpisu); null, když ukázka není. */
  sample_asset_url: string | null;
  sample_lessons: Lesson[];
  content_available: boolean;
};

/* Objednávky / checkout (WP4, kontrakt WP6) */

/** Sanitizovaný souhrn produktu v odpovědích objednávky (žádný obsah). */
export type OrderProductSummary = {
  slug: string;
  title: string;
  subtitle: string | null;
  subject: SubjectRef;
  content_type: ContentType;
  fulfillment_type: FulfillmentType;
  price_czk: number;
};

export type OrderCreateBody = {
  product_slug: string;
  variant?: string | null;
  email: string;
  buyer_name?: string | null;
  consent_terms: boolean;
  consent_digital_delivery: boolean;
};

/** Odpověď `POST /orders`, token pro polling + částka/VS. */
export type OrderCreated = {
  token: string;
  variable_symbol: string;
  amount_czk: number;
  status: OrderStatus;
  payment_method: PaymentMethod | null;
  expires_at: string | null;
  product: OrderProductSummary;
};

export type DeliveryDownload = {
  asset_id: number | string;
  filename: string;
  url: string;
  expires_at: string | null;
};

export type DeliveryLesson = {
  id: number;
  title: string;
  duration_s: number;
  url: string;
  expires_at: string | null;
};

/** Doručení po zaplacení (přidává WP6; do té doby může být `null`/chybět). */
export type OrderDelivery = {
  fulfillment_type: FulfillmentType;
  downloads: DeliveryDownload[];
  lessons: DeliveryLesson[];
  license_key: string | null;
  email_sent_to: string | null;
};

/** Odpověď `GET /orders/{token}`, sanitizovaný stav pro polling. */
export type OrderStatusData = {
  token: string;
  status: OrderStatus;
  amount_czk: number;
  variable_symbol: string;
  payment_method: PaymentMethod | null;
  delivered: boolean;
  paid_at: string | null;
  delivered_at: string | null;
  expires_at: string | null;
  product: OrderProductSummary;
  delivery?: OrderDelivery | null;
};

export type PayQr = {
  method: 'QR';
  spayd: string;
  qr_svg: string;
  account: string | null;
  variable_symbol: string;
  amount_czk: number;
};

export type PayCard = {
  method: 'CARD';
  redirect_url: string;
};

export type PayResult = PayQr | PayCard;

/* Filtry výpisu produktů */

export type ProductFilters = {
  category?: string;
  /** Slug předmětu z číselníku (`?subject=matematika`). */
  subject?: string;
  grade?: string;
  content_type?: ContentType;
  q?: string;
  sort?: ProductSort;
  limit?: number;
  offset?: number;
};

/* Chyba API */

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, params?: Record<string, unknown>): Promise<T> {
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    }
  }

  let res: Response;
  try {
    res = await fetch(url.pathname + url.search, {
      headers: { Accept: 'application/json' },
    });
  } catch {
    throw new ApiError(0, 'Nepodařilo se spojit se serverem.');
  }

  if (!res.ok) {
    let detail = `Chyba serveru (${res.status}).`;
    try {
      const body = (await res.json()) as { detail?: string };
      if (body?.detail) detail = body.detail;
    } catch {
      /* tělo není JSON, ponecháme obecnou hlášku */
    }
    throw new ApiError(res.status, detail);
  }

  return (await res.json()) as T;
}

async function send<T>(path: string, method: 'POST', body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'Nepodařilo se spojit se serverem.');
  }

  if (!res.ok) {
    let detail = `Chyba serveru (${res.status}).`;
    try {
      const data = (await res.json()) as { detail?: unknown };
      if (typeof data?.detail === 'string') detail = data.detail;
      else if (Array.isArray(data?.detail) && data.detail.length > 0) {
        // FastAPI validační chyby → první srozumitelná hláška
        const first = data.detail[0] as { msg?: string };
        if (first?.msg) detail = first.msg;
      }
    } catch {
      /* tělo není JSON, ponecháme obecnou hlášku */
    }
    throw new ApiError(res.status, detail);
  }

  return (await res.json()) as T;
}

/** Absolutní URL k QR obrázku objednávky (alternativa k `qr_svg` stringu). */
export function orderQrSvgUrl(token: string): string {
  return `${API_BASE}/orders/${encodeURIComponent(token)}/qr.svg`;
}

/** Veřejná URL náhledu galerie (GALLERY asset publikovaného produktu). */
export function galleryImageUrl(slug: string, assetId: number): string {
  return `${API_BASE}/products/${encodeURIComponent(slug)}/gallery/${assetId}`;
}

export const api = {
  categories: () => request<Category[]>('/categories'),
  subjects: () => request<Subject[]>('/subjects'),
  products: (filters: ProductFilters = {}) => request<ProductList>('/products', filters),
  product: (slug: string) => request<ProductDetail>(`/products/${encodeURIComponent(slug)}`),
  sample: (slug: string) => request<Sample>(`/products/${encodeURIComponent(slug)}/sample`),

  createOrder: (body: OrderCreateBody) => send<OrderCreated>('/orders', 'POST', body),
  order: (token: string) => request<OrderStatusData>(`/orders/${encodeURIComponent(token)}`),
  pay: (token: string, method: PaymentMethod) =>
    send<PayResult>(`/orders/${encodeURIComponent(token)}/pay`, 'POST', { method }),
  devMarkPaid: (token: string) =>
    send<OrderStatusData>(`/orders/${encodeURIComponent(token)}/dev-mark-paid`, 'POST'),
};
